import { Download } from '@mui/icons-material';
import { IconButton, Link } from '@mui/material';
import React, { useRef } from 'react';
import {
  BooleanField,
  BooleanInput,
  Create,
  Edit,
  Filter,
  FunctionField,
  ImageField,
  NumberField,
  ReferenceField,
  SaveButton,
  Show,
  ShowButton,
  SimpleForm,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  Toolbar,
  useRecordContext,
} from 'react-admin';
import { QRCode } from 'react-qrcode-logo';
import appIcon from '../../assets/images/app-icon.png';
import {
  AdminLocationDetailsFragment,
  CoachLocation,
  Prefix,
} from '../../graphql/types';
import useLocationCoachesQuery from '../../hooks/useLocationCoachesQuery';
import { IDSearch } from '../reusable/IDSearch';
import AutocompleteStudioInput from './components/AutocompleteStudioInput';
import CopyIconButton from './components/CopyIconButton';
import ImageUploadInput from './components/ImageUploadInput';
import InfiniteList from './components/InfiniteList';
import { ListCoaches } from './components/ListCoaches';
import { WriteOnlyDatagrid } from './components/WriteOnlyDatagrid';
import { WriteOnlyEditButton } from './components/WriteOnlyEditButton';

const LocationToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

const LocationFilters: React.FC = (props) => (
  <Filter {...props}>
    <IDSearch label="Name" source="name" alwaysOn />
  </Filter>
);

export const LocationList: React.FC = () => (
  <InfiniteList filters={<LocationFilters />}>
    <WriteOnlyDatagrid bulkActionButtons={false}>
      <TextField source="name" />
      <TextField source="stateCode" />
      <TextField source="mboLocationId" />
      <TextField source="studio.name" label="Studio" />
      <BooleanField source="isHeadquarters" label="HQ" />
      <NumberField source="coaches.count" label="Coaches" />
      <WriteOnlyEditButton />
      <ShowButton />
    </WriteOnlyDatagrid>
  </InfiniteList>
);

const LocationCoachTab: React.FC = () => {
  const record = useRecordContext<AdminLocationDetailsFragment>();
  const { coaches, loadMore, hasNextPage } = useLocationCoachesQuery(
    record?.id,
  );

  return (
    <ListCoaches
      coaches={coaches}
      loadMore={loadMore}
      hasNextPage={hasNextPage}
    />
  );
};

export const LocationShow: React.FC = () => {
  const QRRef = useRef<QRCode>(null);
  return (
    <Show>
      <TabbedShowLayout>
        <Tab label="Basic Info">
          <TextField source="id" />
          <TextField source="name" />
          <TextField source="stateCode" />
          <TextField source="mboLocationId" />
          <BooleanField source="isHeadquarters" label="HQ" />
          <TextField source="contactEmail" label="Contact Email" />
          <ImageField source="gymImage.url" label="Custom Gym Image" />
          <TextField
            source="joinGymInstructions"
            label="Custom Join Gym Instructions"
          />
          <FunctionField
            label="Membership Agreement Link"
            render={({ membershipAgreementLink }: CoachLocation) => {
              return (
                <>
                  {!!membershipAgreementLink && (
                    <Link
                      href={membershipAgreementLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {membershipAgreementLink}
                    </Link>
                  )}
                </>
              );
            }}
          />
          <TextField
            source="postConnectionInstructions"
            label="Custom Confirmation Instructions"
          />
          <ReferenceField source="studio.id" reference="Studio" label="Studio">
            <TextField source="name" />
          </ReferenceField>
          <FunctionField
            label="Location QR Code"
            render={(record) => {
              const url = `https://fit.teamexos.com/location/${record.id}`;
              return (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Link href={url} target="_blank" rel="noopener noreferrer">
                    Open QR Code
                  </Link>
                  <CopyIconButton text={url} fontSize={14} />
                  <IconButton
                    onClick={() =>
                      QRRef.current?.download('png', `${record.name} QR Code`)
                    }
                  >
                    <Download fontSize="small" />
                  </IconButton>
                  {/* QR code needs to be mounted so we can download it. */}
                  <div style={{ display: 'none' }}>
                    <QRCode
                      ref={QRRef}
                      value={url}
                      size={256}
                      logoImage={appIcon}
                      logoHeight={64}
                      logoWidth={64}
                      quietZone={0}
                    />
                  </div>
                </div>
              );
            }}
          />
        </Tab>
        <Tab label="Coaches">
          <LocationCoachTab />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

export const LocationEdit: React.FC = () => (
  <Edit mutationMode="pessimistic" redirect={false}>
    <SimpleForm toolbar={<LocationToolbar />}>
      <TextInput source="id" InputProps={{ disabled: true }} />
      <TextInput source="name" required />
      <TextInput source="stateCode" required />
      <TextInput source="mboLocationId" required />
      <BooleanInput source="isHeadquarters" />
      <AutocompleteStudioInput source="studio.id" label="Studio" isRequired />
      <TextInput source="contactEmail" label="Contact Email" />
      <ImageUploadInput
        source="gymImage"
        prefix={Prefix.Gym}
        label="Custom Gym Image"
        helperText="Upload a rectangular (3:2 aspect ratio) image"
      />
      <TextInput
        source="joinGymInstructions"
        label="Custom Join Gym Instructions"
        multiline
        fullWidth
        helperText="'Uses MBO Registration Flow' clients only. Optionally shown to users prior to connecting this location. Defaults to showing nothing."
      />
      <TextInput
        source="membershipAgreementLink"
        label="Membership Agreement Link"
        fullWidth
        helperText="Shown on the Join Gym screen. Expected if Gym Membership is paid."
      />
      <TextInput
        source="postConnectionInstructions"
        label="Custom Confirmation Instructions"
        multiline
        fullWidth
        helperText="'Uses MBO Registration Flow' clients only. Shown to users after connecting to this location. Defaults to 'You will receive an email to set up your gym membership password.'"
      />
    </SimpleForm>
  </Edit>
);

export const LocationCreate: React.FC = () => (
  <Create>
    <SimpleForm>
      <TextInput source="name" required />
      <TextInput source="stateCode" required />
      <TextInput source="mboLocationId" required />
      <BooleanInput source="isHeadquarters" />
      <AutocompleteStudioInput source="studio.id" label="Studio" isRequired />
      <TextInput source="contactEmail" label="Contact Email" />
      <ImageUploadInput
        source="gymImage"
        prefix={Prefix.Gym}
        label="Custom Gym Image"
        helperText="Upload a rectangular (3:2 aspect ratio) image"
      />
      <TextInput
        source="joinGymInstructions"
        label="Custom Join Gym Instructions"
        multiline
        fullWidth
        helperText="'Uses MBO Registration Flow' clients only. Optionally shown to users prior to connecting this location. Defaults to showing nothing."
      />
      <TextInput
        source="membershipAgreementLink"
        label="Membership Agreement Link"
        fullWidth
        helperText="Shown on the Join Gym screen. Expected if Gym Membership is paid."
      />
      <TextInput
        source="postConnectionInstructions"
        label="Custom Confirmation Instructions"
        multiline
        fullWidth
        helperText="'Uses MBO Registration Flow' clients only. Shown to users after connecting to this location. Defaults to 'You will receive an email to set up your gym membership password.'"
      />
    </SimpleForm>
  </Create>
);
